import { Component, HostListener, Inject, Renderer2 } from '@angular/core';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { AccountService } from "../../account/account.service";
import { ExternalCashOutService } from './externalCashOut.service';
import { AuthService } from "../../../shared/services/auth.service";
import { ToastrService } from 'ngx-toastr';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { CASHOUT_COUNTRY_FIELDS } from './externalCashOut.fields';
import { ActivatedRoute } from '@angular/router';
import { WithdrawPaymentRequest } from './externalCashOut.interface';
import { LogoService } from 'src/app/shared/services/logo.service';
import { DOCUMENT } from '@angular/common';

interface CheckoutCustomization {
  accountId?: string;
  colorButton?: string;
  colorBotton?: string;
  colorWindow?: string;
  urlImagemLogo?: string;
}

@Component({
  selector: 'app-externalCashOut',
  templateUrl: './externalCashOut.component.html',
  styleUrls: ['./externalCashOut.component.scss']
})
export class ExternalCashOutComponent {
  cashOut = new FormGroup({
    documento: new FormControl<string>('', [Validators.required]),
    email: new FormControl<string>('', [Validators.email]),
    nome: new FormControl<string>('', Validators.required),
    valor: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
    taxa: new FormControl<number>(0),
    valorTotal: new FormControl<number>(0),
    paymentMethods: new FormControl<string>('', Validators.required)
  });

  public customization: CheckoutCustomization | null = null;
  public touchStartX = 0;
  public touchEndX = 0;
  public mouseStartX = 0;
  public mouseEndX = 0;
  public CASHOUT_COUNTRY_FIELDS = CASHOUT_COUNTRY_FIELDS;
  public countryForm!: FormGroup;
  public showCountryForm = false;
  public showSuccess = false;
  public isSubmitted = false;
  public currentStep = 0;
  public idempotencyKey = '';
  public originCurrency = '';
  public transactionId = '';
  public nomeFeedBack = '';
  public documentFeedBack = '';
  public valorFeedBack = '';
  public origemFeedBack = '';
  public depositTaxValue = 0;
  public depositTaxPercent = 0;
  public isCountryFormSubmitted = false;
  public isLoaded = true;
  public originList: { name: string; value: string }[] = [];
  public bankCodeOptions: { name: string; value: string }[] = [];
  public isLoadingBankCodes = false;
  private prefillPerson: any = null;
  public currencyPrefix = '';

  public copDocumentTypes = [
    { value: 'CC', label: 'CC' },
    { value: 'TI', label: 'TI' },
    { value: 'CE', label: 'CE' },
    { value: 'PA', label: 'PA' },
    { value: 'NIT', label: 'NIT' },
  ];

  public clpDocumentTypes = [
    { value: 'CI', label: 'CI' },
    { value: 'RUN', label: 'RUN' },
    { value: 'DNI', label: 'DNI' },
    { value: 'RUT', label: 'RUT' },
  ];

  public clpAccountTypes = [
    { value: '1', label: 'Corriente' },
    { value: '2', label: 'Vista/Cuenta' },
    { value: '3', label: 'Ahorro' },
  ];

  public mxnAccountTypes = [
    { value: '40', label: 'Clabe' },
    { value: '3', label: 'Bank card' },
  ];

  public penDocumentTypes = [
    { value: 'DNI', label: 'DNI' },
    { value: 'CE', label: 'CE' },
    { value: 'RUC', label: 'RUC' },
    { value: 'PAS', label: 'PAS' },
  ];

  public penAccountTypes = [
    { value: '1', label: 'CORRIENTE' },
    { value: '2', label: 'AHORROS' },
  ];

  constructor(
    private accountService: AccountService,
    private externalCashOutService: ExternalCashOutService,
    private authService: AuthService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private logo: LogoService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) { }

  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent) {
    this.touchStartX = event.changedTouches[0].clientX;
  }

  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent) {
    this.touchEndX = event.changedTouches[0].clientX;
    this.handleSwipe(this.touchEndX - this.touchStartX);
  }

  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    this.mouseStartX = event.clientX;
  }

  @HostListener('mouseup', ['$event'])
  onMouseUp(event: MouseEvent) {
    this.mouseEndX = event.clientX;
    this.handleSwipe(this.mouseEndX - this.mouseStartX);
  }

  ngOnInit(): void {
    this.cashOut.controls.paymentMethods.valueChanges.subscribe(() => {
      this.loadBankCodesForCurrentSelection();
    });

    this.route.queryParamMap.subscribe(qp => {
      const id = qp.get('id');
      if (id) {
        this.transactionId = id;
        this.getTransactionById(id);
      }
    });
  }

  get canGoToBankStep(): boolean {
    return this.cashOut.valid;
  }

  private handleSwipe(deltaX: number): void {
    const threshold = 50;

    if (Math.abs(deltaX) < threshold) {
      return;
    }

    if (deltaX < 0) {
      if (this.currentStep === 0 && this.canGoToBankStep) {
        this.goToStep(1);
      }
    } else {
      if (this.currentStep === 1) {
        this.goToStep(0);
      }
    }
  }

  public getTransactionById(id: string) {
    this.isLoaded = false;
    this.externalCashOutService.getTransaction(id).subscribe({
      next: (res) => {
        this.idempotencyKey = res?.idempotencyKey ?? '';
        this.originCurrency = (res?.originCurrency ?? this.originCurrency ?? '').toUpperCase();
        this.customization = res?.customizationColor || null;
        this.currencyPrefix = this.resolveCurrencyPrefix(this.originCurrency);
        this.logo.setLogo(this.customization?.urlImagemLogo || null);
        this.applyPageBackground(this.customization?.colorBotton);

        this.cashOut.patchValue({
          nome: res?.name ?? '',
          documento: res?.document ?? '',
          email: res?.email ?? '',
          valor: res?.amount ?? null,
          paymentMethods: res?.origin ?? ''
        });

        const paymentMethods: string[] = res?.listPayMethod ?? [];
        const isPhp = this.originCurrency === 'PHP';
        this.originList = paymentMethods.map(m => ({
          name: isPhp ? this.formatPaymentMethodLabel(m) : m,
          value: m
        }));

        if (this.originList.length) {
          this.cashOut.controls.paymentMethods.setValue(this.originList[0].value, { emitEvent: false });
        } else {
          this.cashOut.controls.paymentMethods.reset(null, { emitEvent: false });
        }

        this.prefillPerson = null;

        this.loadBankCodesForCurrentSelection();

        this.isLoaded = true;
      },
      error: () => {
        this.toastr.error('Não foi possível carregar o checkout.');
        this.isLoaded = true;
      }
    });
  }

  private resolveCurrencyPrefix(origin: string | null | undefined): string {
    const oc = (origin || '').toUpperCase();

    switch (oc) {
      case 'BRL': return 'R$';
      case 'CLP': return '$';
      case 'COP': return '$';
      case 'MXN': return '$';
      case 'PEN': return 'S/';
      case 'PHP': return '₱';
      default: return '';
    }
  }

  private formatPaymentMethodLabel(method: string): string {
    return method.replace(/^PH_/, '').replace(/_/g, ' ');
  }

  onConfirmCheckout(): void {
    this.isSubmitted = true;
    if (this.cashOut.invalid) return;

    this.buildCountryForm();
    this.currentStep = 1;
    this.prefillCountryForm();
    this.loadBankCodesForCurrentSelection();

    const oc = (this.originCurrency || this.authService.getOriginCurrency() || '').toUpperCase();

    const email = this.cashOut.controls.email.value?.toString().trim();
    if (email) {
      this.externalCashOutService.getPersonUser(email, oc).subscribe({
        next: (resp) => {
          this.prefillPerson = resp || null;
          this.prefillCountryForm();
        },
        error: () => { }
      });
    }
  }

  private loadBankCodesForCurrentSelection(): void {
    const oc = (this.originCurrency || this.authService.getOriginCurrency() || '').toUpperCase();
    const cfg = this.CASHOUT_COUNTRY_FIELDS[oc] || [];

    // Agora: qualquer país com bankCode OU COP (que usa bankName) busca bankcodes
    const usesBankCode =
      cfg.some(f => f.name === 'bankCode') || oc === 'COP';

    if (!usesBankCode) {
      this.bankCodeOptions = [];
      return;
    }

    const payMethod = (this.cashOut.controls.paymentMethods.value || '').toString().trim();
    if (!payMethod) {
      this.bankCodeOptions = [];
      return;
    }

    this.isLoadingBankCodes = true;

    this.externalCashOutService.getBankCodes(oc, payMethod).subscribe({
      next: (res) => {
        this.bankCodeOptions = (res?.bankCode || []).map((b: any) => ({
          name: String(b.bankName),
          value: String(b.id)
        }));
        this.isLoadingBankCodes = false;

        if (this.countryForm) {
          // Para países com campo bankCode
          if (this.countryForm.contains('bankCode')) {
            const pre = this.prefillPerson?.bankCode ? String(this.prefillPerson.bankCode) : '';
            if (pre && this.bankCodeOptions.some(o => o.value === pre)) {
              this.countryForm.patchValue({ bankCode: pre }, { emitEvent: false });
            } else if (this.bankCodeOptions.length) {
              this.countryForm.patchValue({ bankCode: this.bankCodeOptions[0].value }, { emitEvent: false });
            }
          }

          if (oc === 'COP' && this.countryForm.contains('bankName')) {
            const preCop =
              this.prefillPerson?.bankCode
                ? String(this.prefillPerson.bankCode)
                : this.countryForm.get('bankName')?.value;

            if (preCop && this.bankCodeOptions.some(o => o.value === preCop)) {
              this.countryForm.patchValue({ bankName: preCop }, { emitEvent: false });
            } else if (this.bankCodeOptions.length) {
              this.countryForm.patchValue(
                { bankName: this.bankCodeOptions[0].value },
                { emitEvent: false }
              );
            }
          }
        }
      },
      error: () => {
        this.isLoadingBankCodes = false;
      }
    });
  }

  private buildCountryForm(): void {
    const oc = (this.originCurrency || '').toUpperCase();
    const cfg = CASHOUT_COUNTRY_FIELDS[oc] || [];
    const group: any = {};

    cfg.forEach(f => group[f.name] = new FormControl('', f.validators));
    this.countryForm = new FormGroup(group);
    this.isCountryFormSubmitted = false;

    if (oc === 'COP' && this.countryForm.contains('documentType')) {
      const current = this.countryForm.get('documentType')?.value;
      if (!current && this.copDocumentTypes.length) {
        this.countryForm.patchValue(
          { documentType: this.copDocumentTypes[0].value },
          { emitEvent: false }
        );
      }
    }

    if (oc === 'CLP') {
      if (this.countryForm.contains('documentType') && this.clpDocumentTypes?.length) {
        const curDoc = this.countryForm.get('documentType')?.value;
        if (!curDoc) {
          this.countryForm.patchValue(
            { documentType: this.clpDocumentTypes[0].value },
            { emitEvent: false }
          );
        }
      }

      if (this.countryForm.contains('accountType') && this.clpAccountTypes?.length) {
        const curAcc = this.countryForm.get('accountType')?.value;
        if (!curAcc) {
          this.countryForm.patchValue(
            { accountType: this.clpAccountTypes[0].value },
            { emitEvent: false }
          );
        }
      }
    }

    if (oc === 'MXN' && this.countryForm.contains('accountType') && this.mxnAccountTypes?.length) {
      const curAcc = this.countryForm.get('accountType')?.value;
      if (!curAcc) {
        this.countryForm.patchValue(
          { accountType: this.mxnAccountTypes[0].value },
          { emitEvent: false }
        );
      }
    }

    if (oc === 'PEN') {
      if (this.countryForm.contains('documentType') && this.penDocumentTypes?.length) {
        const curDoc = this.countryForm.get('documentType')?.value;
        if (!curDoc) {
          this.countryForm.patchValue(
            { documentType: this.penDocumentTypes[0].value },
            { emitEvent: false }
          );
        }
      }

      if (this.countryForm.contains('accountType') && this.penAccountTypes?.length) {
        const curAcc = this.countryForm.get('accountType')?.value;
        if (!curAcc) {
          this.countryForm.patchValue(
            { accountType: this.penAccountTypes[0].value },
            { emitEvent: false }
          );
        }
      }
    }
  }




  private prefillCountryForm(): void {
    if (!this.countryForm) return;

    const oc = (this.originCurrency || '').toUpperCase();
    const patch: any = {};

    if (oc === 'PHP') {
      const name = this.cashOut.controls.nome.value?.toString().trim();
      if (name) {
        patch.accountName = name;
      }
      this.countryForm.patchValue(patch, { emitEvent: false });
      return;
    }

    if (oc === 'COP') {
      const nome = this.cashOut.controls.nome.value?.toString().trim();
      const doc = this.cashOut.controls.documento.value?.toString().trim();
      const email = this.cashOut.controls.email.value?.toString().trim();

      if (nome && this.countryForm.contains('customerName')) {
        patch.customerName = nome;
      }
      if (email && this.countryForm.contains('customerEmail')) {
        patch.customerEmail = email;
      }
      if (doc && this.countryForm.contains('documentNumber')) {
        patch.documentNumber = doc;
      }
    }

    if (oc === 'PEN') {
      const nome = this.cashOut.controls.nome.value?.toString().trim();
      const doc = this.cashOut.controls.documento.value?.toString().trim();
      const email = this.cashOut.controls.email.value?.toString().trim();

      if (nome && this.countryForm.contains('accountName')) {
        patch.accountName = nome;
      }
      if (email && this.countryForm.contains('customerEmail')) {
        patch.customerEmail = email;
      }
      if (doc && this.countryForm.contains('documentNumber')) {
        patch.documentNumber = doc;
      }
    }

    if (this.prefillPerson) {
      const p = this.prefillPerson;

      if (oc === 'CLP') {
        if (p.customerPhone) patch.customerPhone = p.customerPhone;
        if (p.customerEmail) patch.customerEmail = p.customerEmail;
        if (p.documentNumberBank) patch.documentNumber = p.documentNumberBank;
        if (p.documentType) patch.documentType = p.documentType;
        if (p.bankCode) patch.bankCode = String(p.bankCode);
        if (p.name) patch.accountName = p.name;
        if (p.accountNoBank) patch.accountNo = p.accountNoBank;
        if (p.accountTypeBank) patch.accountType = p.accountTypeBank;

      } else if (oc === 'COP') {
        if (p.bankCode && this.countryForm.contains('bankName')) {
          patch.bankName = String(p.bankCode);
        }
        if (p.accountNoBank) patch.accountNo = p.accountNoBank;
        if (p.name) patch.customerName = p.name;
        if (p.customerPhone) patch.customerPhone = p.customerPhone;
        if (p.customerEmail) patch.customerEmail = p.customerEmail;
        if (p.documentNumberBank) patch.documentNumber = p.documentNumberBank;
        if (p.documentType) patch.documentType = p.documentType;

      } else if (oc === 'MXN') {
        if (p.bankCode) patch.bankCode = String(p.bankCode);
        if (p.accountNoBank) patch.accountNo = p.accountNoBank;
        if (p.accountTypeBank) patch.accountType = p.accountTypeBank;

      } else if (oc === 'PEN') {
        if (p.customerPhone) patch.customerPhone = p.customerPhone;
        if (p.customerEmail) patch.customerEmail = p.customerEmail;
        if (p.documentNumberBank) patch.documentNumber = p.documentNumberBank;
        if (p.documentType) patch.documentType = p.documentType;
        if (p.name) patch.accountName = p.name;
        if (p.accountNoBank) patch.accountNo = p.accountNoBank;
        if (p.accountTypeBank) patch.accountType = p.accountTypeBank;
        if (p.cciNo) patch.cciNo = p.cciNo;
        if (p.bankCode) patch.bankCode = String(p.bankCode);
      }
    }

    if (oc === 'MXN') {
      const nome = this.cashOut.controls.nome.value?.toString().trim();
      if (nome && this.countryForm.contains('accountName')) {
        patch.accountName = nome;
      }
    }

    if (Object.keys(patch).length) {
      this.countryForm.patchValue(patch, { emitEvent: false });
    }
  }




  confirmCountryForm(): void {
    this.isCountryFormSubmitted = true;
    if (!this.countryForm || this.countryForm.invalid) return;

    const base = this.buildBaseWithdrawPayment();
    const c = this.countryForm.value as any;

    switch ((this.originCurrency || '').toUpperCase()) {
      case 'CLP':
        base.bankTransferExtraClp = {
          customerPhone: c.customerPhone,
          customerEmail: c.customerEmail,
          documentNumber: c.documentNumber,
          documentType: c.documentType,
          bankCode: c.bankCode,
          accountName: c.accountName,
          accountNo: c.accountNo,
          accountType: c.accountType,
        };
        break;

      case 'COP': {
        let bankName = c.bankName;
        if (c.bankName && this.bankCodeOptions?.length) {
          const selected = this.bankCodeOptions.find(
            b => b.value === c.bankName || b.name === c.bankName
          );
          if (selected) {
            bankName = selected.name;
          }
        }
        base.bankTransferExtraCop = {
          bankName,
          accountNo: c.accountNo,
          customerName: c.customerName,
          customerPhone: c.customerPhone,
          customerEmail: c.customerEmail,
          documentNumber: c.documentNumber,
          documentType: c.documentType,
        };
        break;
      }

      case 'MXN': {
        const selectedBank = this.bankCodeOptions.find(b => b.value === c.bankCode);
        const bankName = selectedBank ? selectedBank.name : '';

        base.bankTransferExtraMxn = {
          bankCode: c.bankCode,
          bankName,
          accountNo: c.accountNo,
          accountName: c.accountName,
          accountType: c.accountType,
        };
        break;
      }

      case 'PEN':
        base.bankTransferExtraPen = {
          customerPhone: c.customerPhone,
          customerEmail: c.customerEmail,
          documentNumber: c.documentNumber,
          documentType: c.documentType,
          accountName: c.accountName,
          accountNo: c.accountNo,
          accountType: c.accountType,
          cciNo: c.cciNo,
          bankCode: c.bankCode,
        };
        break;

      case 'PHP':
        base.bankTransferExtraPhP = {
          accountName: c.accountName,
          accountNo: c.accountNo,
          bankCode: c.bankCode,
        };
        break;
    }

    this.sendWithdrawPayment(base);
  }


  private buildBaseWithdrawPayment(): WithdrawPaymentRequest {
    return {
      id: this.transactionId,
      origin: (this.cashOut.value.paymentMethods ?? '')
        .toString()
        .trim()
        .toUpperCase()
    };
  }

  private sendWithdrawPayment(body: WithdrawPaymentRequest): void {
    this.isLoaded = false;
    this.externalCashOutService.updateTransaction(body).subscribe({
      next: (res) => {
        this.isLoaded = true;
        if (res?.success) {
          this.toastr.success(res.message || 'Sucesso!');
          this.showCountryForm = false;
          this.showSuccess = true;
          this.currentStep = 0;
        } else {
          this.toastr.error(res?.message || 'Erro');
        }
      },
      error: () => {
        this.isLoaded = true;
        this.toastr.error('Erro inesperado.');
      }
    });
  }

  goToStep(step: number): void {
    if (step === this.currentStep) return;

    if (step === 0) {
      this.currentStep = 0;
      this.isCountryFormSubmitted = false;
      return;
    }

    if (step === 1 && this.canGoToBankStep) {
      this.onConfirmCheckout();
    }
  }

  public valorChanged() {
    const valor = Number(this.cashOut.controls.valor.value);
    this.cashOut.controls.valor.setValue(valor);
    let taxa = (valor * this.depositTaxPercent) / 100;
    const total = valor > 0 ? (valor - (taxa + this.depositTaxValue)) : 0;
    this.cashOut.controls.taxa.setValue(valor > 0 ? (taxa + this.depositTaxValue) : 0);
    this.cashOut.controls.valorTotal.setValue(total);
  }

  public formIsValid(): boolean {
    if (
      (this.cashOut.controls.documento.value!.length === 11 && !cpf.isValid(this.cashOut.controls.documento.value!)) ||
      (this.cashOut.controls.documento.value!.length === 14 && !cnpj.isValid(this.cashOut.controls.documento.value!))
    ) {
      this.cashOut.controls.documento.setErrors(Validators.required);
      this.documentFeedBack = 'Documento inválido!';
    }
    if (this.cashOut.controls.documento.hasError('required')) {
      this.documentFeedBack = 'Documento é obrigatório!';
    }
    if (this.cashOut.controls.nome.invalid) {
      this.nomeFeedBack = 'Nome é obrigatório!';
    }
    if (this.cashOut.controls.valor.hasError('min')) {
      this.valorFeedBack = 'Valor inválido - Valor mínimo R$ 1,00!';
    } else if (this.cashOut.controls.valor.invalid) {
      this.valorFeedBack = 'Valor é obrigatório!';
    }
    if (this.cashOut.controls.paymentMethods.hasError('required')) {
      this.origemFeedBack = 'Origem é obrigatória!';
    }
    return this.cashOut.valid;
  }

  get buttonColor(): string {
    return this.customization?.colorButton || 'var(--primary-bg-color)';
  }

  get pageBackgroundColor(): string {
    return this.customization?.colorBotton || '#f5f5f5';
  }

  get cardColor(): string {
    return this.customization?.colorWindow || '#ffffff';
  }

  get hasLogo(): boolean {
    return !!this.customization?.urlImagemLogo;
  }

  get logoUrl(): string {
    return this.customization?.urlImagemLogo || '';
  }

  getStepIconStyle(stepIndex: number): { [key: string]: string } {
    if (this.currentStep === stepIndex) {
      return {
        'background-color': this.buttonColor,
        'border-color': this.buttonColor,
        'color': '#fff'
      };
    }

    if (this.currentStep > stepIndex) {
      return {
        'background-color': '#16a34a',
        'border-color': '#16a34a',
        'color': '#fff'
      };
    }

    return {};
  }

  private applyPageBackground(color?: string | null): void {
    const body = this.document.body;

    if (color) {
      this.renderer.setStyle(body, 'background-color', color);
    } else {
      this.renderer.removeStyle(body, 'background-color');
    }
  }
}
